import React from 'react';

import { di } from 'react-magnetic-di';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Radio from '@atlaskit/radio/radio';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { asNumberValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import { asFormRankingField } from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { ScrollContainer } from '../../../../../../../../common/ui/scroll-container';
import {
	getRatingIconComponent,
	getRatingIconFromOptions,
	getSelectedRatingIconComponent,
} from '../../../../../../../../common/utils/rating/utils';
import { useFormField } from '../../../../../../../../controllers/form-renderer-store';

const styles = cssMap({
	container: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

export const PreviewRatingInput = ({
	fieldId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useFormField);

	const formField = useFormField({ fieldId });
	const formRankingField = asFormRankingField(formField);

	if (formRankingField === undefined) {
		return <></>;
	}

	const isRatingScaleA11yFixesEnabled = fg('proforma_rating_scale_a11y_fixes');
	const currentValue = asNumberValue(formRankingField.value);
	const selectedOptionId = currentValue?.number?.toString() ?? null;
	const selectedNumericId = selectedOptionId !== null ? Number(selectedOptionId) : null;
	const ratingIcon = getRatingIconFromOptions(formRankingField.options);
	const UnselectedIcon = getRatingIconComponent(ratingIcon);
	const SelectedIcon = getSelectedRatingIconComponent(ratingIcon);

	return (
		<ScrollContainer>
			<Box xcss={styles.container} testId="field-input-rating-preview">
				<Inline
					space="space.400"
					role={isRatingScaleA11yFixesEnabled ? 'radiogroup' : undefined}
					aria-label={isRatingScaleA11yFixesEnabled ? formRankingField.label : undefined}
				>
					{formRankingField.options.map((option) => {
						const numericId = Number(option.id);
						const isFilled =
							selectedNumericId !== null &&
							!Number.isNaN(numericId) &&
							numericId <= selectedNumericId;
						const IconComponent = isFilled ? SelectedIcon : UnselectedIcon;
						const inputId = `${fieldId}-${option.id}`;
						return (
							<Stack
								key={option.id}
								alignInline="center"
								space="space.100"
								testId={`field-input-rating-preview-option-${option.id}-${fieldId}`}
							>
								<VisuallyHidden>
									<Radio
										id={inputId}
										value={option.id}
										label={option.id}
										name={fieldId}
										isChecked={option.id === selectedOptionId}
										isDisabled
									/>
								</VisuallyHidden>
								<IconButton
									appearance="subtle"
									icon={(iconProps) => (
										<IconComponent {...iconProps} color={token('color.icon.disabled')} />
									)}
									label={option.id}
									isDisabled
									aria-hidden
									tabIndex={-1}
								/>
								<Text size="small" align="center">
									{option.id}
								</Text>
							</Stack>
						);
					})}
				</Inline>
			</Box>
		</ScrollContainer>
	);
};
